import React, { useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AlertTriangle, Loader2, CheckCircle2 } from "lucide-react";

/**
 * Persistent banner shown on the dashboard when the logged-in user hasn't
 * verified their email. Includes a "Resend link" button that hits
 * /auth/resend-verification (rate-limited server-side to 3/hour). Hides
 * itself for verified users, admins/staff (no verification requirement), and
 * anonymous visitors.
 */
export default function VerifyEmailBanner() {
  const { user } = useAuth();
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  if (!user || user === false) return null;
  if (user.email_verified !== false) return null;   // grandfathered or Google users
  if (user.role && user.role !== "customer") return null;

  const resend = async () => {
    setSending(true); setError("");
    try {
      await api.post("/auth/resend-verification");
      setSent(true);
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not send email — please try again in a minute.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      role="alert"
      data-testid="verify-email-banner"
      className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900"
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 mt-0.5 shrink-0" aria-hidden />
        <div className="text-sm">
          <div className="font-semibold">Please verify your email address</div>
          <div className="text-[13px] text-amber-800/90 mt-0.5">
            We sent a verification link to <span className="font-medium">{user.email}</span>. You need to confirm your email before you can place an order.
          </div>
        </div>
      </div>
      <div className="shrink-0 flex items-center gap-2">
        {sent ? (
          <span className="inline-flex items-center gap-1.5 text-emerald-700 text-sm font-medium" data-testid="verify-email-banner-sent">
            <CheckCircle2 className="w-4 h-4" /> Sent — check your inbox
          </span>
        ) : (
          <button
            onClick={resend}
            disabled={sending}
            data-testid="verify-email-banner-resend"
            className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 disabled:bg-amber-400 text-white text-sm font-semibold px-3.5 py-2 rounded-md"
          >
            {sending && <Loader2 className="w-4 h-4 animate-spin" />}
            {sending ? "Sending…" : "Resend link"}
          </button>
        )}
      </div>
      {error && (
        <div className="w-full text-xs text-red-700 mt-1" data-testid="verify-email-banner-error">{error}</div>
      )}
    </div>
  );
}
